import FloatingPanel from "@imminently/decisively-ui/FloatingPanel";
import { flip, offset } from "@udecode/plate-floating";
import {
  FloatingLink,
  LaunchIcon,
  LinkIcon,
  LinkOffIcon,
  ShortTextIcon,
  submitFloatingLink,
  useFloatingLinkSelectors,
} from "@udecode/plate-link";
import { useEditorRef } from "@udecode/plate-core";

import Button from "@imminently/decisively-ui/Button";

type PlateFloatingLinkPanelProps = {
  readOnly?: boolean;
};

const LINK_PANEL_FLOATING_OPTIONS = {
  placement: "bottom-start" as const,
  middleware: [
    offset(64),
    flip({
      padding: 96,
    }),
  ],
};

const LinkInput = () => {
  const editor = useEditorRef();

  return (
    <div className="flex w-[330px] flex-col">
      <label className="flex items-center gap-2 px-3 py-2">
        <LinkIcon width={18} className="text-muted-foreground" />
        <FloatingLink.UrlInput
          className="h-8 flex-1 border-0 bg-transparent p-0 text-sm outline-none"
          placeholder="Paste link"
        />
      </label>
      <div className="h-px bg-border" />
      <label className="flex items-center gap-2 px-3 py-2">
        <ShortTextIcon width={18} className="text-muted-foreground" />
        <FloatingLink.TextInput
          className="h-8 flex-1 border-0 bg-transparent p-0 text-sm outline-none"
          placeholder="Text to display"
        />
      </label>
      <div className="flex justify-end border-t border-border p-2">
        <Button
          size="sm"
          intent="primary"
          label="Finish"
          onMouseDown={(event) => {
            event.preventDefault();
            submitFloatingLink(editor);
          }}
        >
          Finish
        </Button>
      </div>
    </div>
  );
};

const LinkActions = () => (
  <div className="flex items-center gap-1 p-1">
    <FloatingLink.EditButton asChild>
      <Button size="sm" variant="ghost" label="Edit link">
        Edit link
      </Button>
    </FloatingLink.EditButton>
    <div className="h-5 w-px bg-border" />
    <FloatingLink.OpenLinkButton asChild>
      <Button icon size="sm" variant="ghost" label="Open link">
        <LaunchIcon width={18} />
      </Button>
    </FloatingLink.OpenLinkButton>
    <div className="h-5 w-px bg-border" />
    <FloatingLink.UnlinkButton asChild>
      <Button icon size="sm" variant="ghost" label="Remove link">
        <LinkOffIcon width={18} />
      </Button>
    </FloatingLink.UnlinkButton>
  </div>
);

export const PlateFloatingLinkPanel = ({ readOnly }: PlateFloatingLinkPanelProps) => {
  const isEditing = useFloatingLinkSelectors().isEditing();

  if (readOnly) {
    return null;
  }

  return (
    <>
      <FloatingLink.InsertRoot floatingOptions={LINK_PANEL_FLOATING_OPTIONS}>
        <FloatingPanel className="p-0">
          <LinkInput />
        </FloatingPanel>
      </FloatingLink.InsertRoot>
      <FloatingLink.EditRoot floatingOptions={LINK_PANEL_FLOATING_OPTIONS}>
        <FloatingPanel className="p-0">
          {isEditing ? <LinkInput /> : <LinkActions />}
        </FloatingPanel>
      </FloatingLink.EditRoot>
    </>
  );
};
